import { createContext, useCallback, useContext, useState } from 'react';

import { Popover as BasePopover } from '@base-ui/react/popover';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import { popoverPanelRecipe, popoverPositionerRecipe } from './Popover.recipe';

import type { PopoverContentProps, PopoverProps } from './types';

/** The root owns `open` so `PopoverContent` can re-resolve the portal's theme each time it opens. */
const PopoverOpenContext = createContext(false);

/**
 * Design System 2.0 popover — an anchored surface that holds anything: a row of reactions, a colour
 * grid, a short form, a filter panel.
 *
 * Designed in Figma as `Popover` (file 5aSkE0U4TeWbuIZDvccHjc, node 1970:124668): one
 * Content slot on Menu's surface.
 *
 * Use `Menu` instead when the content is a list of commands — Menu adds arrow-key navigation between
 * rows and closes on a pick, which a popover deliberately does not. Use `Tooltip` for hover-only text,
 * and `Dialog` when the page behind should be blocked.
 *
 * ```tsx
 * <Popover>
 *   <PopoverTrigger render={<IconButton aria-label="Reactions">…</IconButton>} />
 *   <PopoverContent side="top" aria-label="Reactions">…</PopoverContent>
 * </Popover>
 * ```
 *
 * The panel is `role="dialog"`, so `PopoverContent` needs an `aria-label` (or `aria-labelledby`).
 */
export const Popover = ({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  onOpenChangeComplete,
  children,
}: PopoverProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange]
  );

  return (
    <PopoverOpenContext.Provider value={open}>
      <BasePopover.Root open={open} onOpenChange={handleOpenChange} onOpenChangeComplete={onOpenChangeComplete}>
        {children}
      </BasePopover.Root>
    </PopoverOpenContext.Provider>
  );
};

// Base UI's own parts, unwrapped: they already forward refs and accept `render`.
export const PopoverTrigger = BasePopover.Trigger;
export const PopoverClose = BasePopover.Close;

export const PopoverContent = ({
  children,
  side = 'bottom',
  align = 'center',
  anchor,
  initialFocus,
  finalFocus,
  className,
  ...rest
}: PopoverContentProps) => {
  const open = useContext(PopoverOpenContext);
  // With no explicit anchor, a hidden sentinel at the call site stands in for the trigger.
  const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(
    open,
    anchor instanceof Element ? anchor : undefined
  );

  return (
    <>
      <span ref={sentinelRef} hidden />
      <BasePopover.Portal className={inheritedDarkClass}>
        <BasePopover.Positioner
          anchor={anchor}
          side={side}
          align={align}
          // Shared popup spacing: 4px of air between the trigger and the panel.
          sideOffset={4}
          className={popoverPositionerRecipe()}
        >
          <BasePopover.Popup
            {...rest}
            className={cn(popoverPanelRecipe(), className)}
            data-part="popover"
            finalFocus={finalFocus}
            initialFocus={initialFocus}
            // Portalled to <body>, but React events still bubble up the React tree — without this a
            // click inside reaches the call site's own onClick (see the same guard in `Menu`).
            onClick={(event) => {
              event.stopPropagation();
              rest.onClick?.(event);
            }}
          >
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </>
  );
};
